import { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap, registerGsap } from '../../anim/gsapSetup';
import { Eyebrow } from '../common/Eyebrow';
import { Icon } from '../common/Icon';
import { LeafMark } from '../common/Logo';
import { Reveal } from '../animation/Reveal';
import { Spinner } from '../common/Spinner';
import { ProductImage } from '../product/ProductImage';
import { categoryBySlug } from '../../lib/categories';
import { primaryVariant, productImageAlt } from '../../lib/image';
import { onDark } from '../../lib/slug';
import { useFeaturedProducts } from '../../hooks';
import type { Product } from '../../types';

/**
 * SEO-first featured product grid. Every card is a real, crawlable <article> rendered up-front by
 * React — GSAP only enhances existing markup. Data is LIVE from the backend (useFeaturedProducts).
 */
function FeaturedCard({ product }: { product: Product }) {
  const category = categoryBySlug(product.categorySlug);
  const variant = primaryVariant(product);
  const accent = product.accentColor;
  const accentText = onDark(accent);

  return (
    <article
      data-fcard
      className="group flex h-full flex-col overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] shadow-soft transition-colors duration-500 hover:border-white/25 [perspective:1200px]"
    >
      <div data-fcard-stage className="relative aspect-square overflow-hidden bg-white [transform-style:preserve-3d]">
        {category?.name && (
          <p className="absolute left-4 top-4 z-20 inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-[0.68rem] font-semibold text-ink-700 shadow-sm backdrop-blur ring-1 ring-black/[0.04]">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} aria-hidden="true" />
            {category.name}
          </p>
        )}
        <div data-fcard-img className="absolute inset-0 flex items-center justify-center p-6">
          <ProductImage
            src={variant.image}
            alt={productImageAlt(product, variant)}
            accent={accent}
            name={product.name}
            className="h-full w-full transition-transform duration-500 group-hover:scale-[1.05]"
            sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 360px"
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-6">
        <div>
          <h3 className="font-display text-2xl font-semibold text-white">{product.name}</h3>
          {product.tagline && (
            <p className="mt-1 text-sm font-semibold" style={{ color: accentText }}>
              {product.tagline}
            </p>
          )}
        </div>

        {product.purpose && <p className="text-[0.95rem] leading-relaxed text-white/70">{product.purpose}</p>}

        {product.benefits.length > 0 && (
          <ul className="mt-1 space-y-2">
            {product.benefits.slice(0, 3).map((benefit) => (
              <li key={benefit} className="flex gap-2 text-sm leading-relaxed text-white/75">
                <Icon name="Check" className="mt-0.5 h-4 w-4 shrink-0" style={{ color: accentText }} />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        )}

        <ul className="mt-1 flex flex-wrap gap-1.5" aria-label={`Available pack sizes for ${product.name}`}>
          {product.variants.map((v) => (
            <li key={v.id} className="rounded-full border border-white/15 px-2.5 py-1 text-xs font-medium text-white/70">
              {v.packSize}
            </li>
          ))}
        </ul>

        <Link
          to={`/catalog/${product.slug}`}
          className="mt-auto inline-flex items-center gap-1.5 pt-3 text-sm font-semibold text-white transition-colors hover:text-brand-300"
        >
          View {product.name} details
          <Icon name="ArrowRight" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </article>
  );
}

export function FeaturedProducts() {
  const { data: products = [], isLoading } = useFeaturedProducts(6);
  const scopeRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const scope = scopeRef.current;
    if (!scope || !products.length) return;
    registerGsap();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    const ctx = gsap.context(() => {
      if (!reduced) {
        gsap.to('[data-fp-leaf]', {
          yPercent: 22,
          rotate: '+=8',
          ease: 'none',
          scrollTrigger: { trigger: scope, start: 'top bottom', end: 'bottom top', scrub: true },
        });
      }

      const removers: Array<() => void> = [];
      if (!reduced && canHover) {
        gsap.utils.toArray<HTMLElement>('[data-fcard]').forEach((card) => {
          const stage = card.querySelector<HTMLElement>('[data-fcard-stage]');
          const img = card.querySelector<HTMLElement>('[data-fcard-img]');
          const rx = gsap.quickTo(stage, 'rotationX', { duration: 0.5, ease: 'power2.out' });
          const ry = gsap.quickTo(stage, 'rotationY', { duration: 0.5, ease: 'power2.out' });
          const ix = gsap.quickTo(img, 'xPercent', { duration: 0.6, ease: 'power2.out' });
          const iy = gsap.quickTo(img, 'yPercent', { duration: 0.6, ease: 'power2.out' });
          const onEnter = () => gsap.set([stage, img], { willChange: 'transform' });
          const onMove = (e: MouseEvent) => {
            const r = card.getBoundingClientRect();
            const px = (e.clientX - r.left) / r.width - 0.5;
            const py = (e.clientY - r.top) / r.height - 0.5;
            ry(px * 8);
            rx(-py * 8);
            ix(px * 5);
            iy(-py * 5);
          };
          const onLeave = () => {
            rx(0);
            ry(0);
            ix(0);
            iy(0);
            gsap.set([stage, img], { willChange: 'auto' });
          };
          card.addEventListener('mouseenter', onEnter);
          card.addEventListener('mousemove', onMove);
          card.addEventListener('mouseleave', onLeave);
          removers.push(() => {
            card.removeEventListener('mouseenter', onEnter);
            card.removeEventListener('mousemove', onMove);
            card.removeEventListener('mouseleave', onLeave);
          });
        });
      }
      return () => removers.forEach((fn) => fn());
    }, scope);

    return () => ctx.revert();
  }, [products]);

  return (
    <section
      ref={scopeRef}
      aria-labelledby="product-range-heading"
      className="grain relative overflow-hidden bg-night py-section text-white"
      style={{ ['--leaf-vein' as string]: '#06231A' }}
    >
      <span data-fp-leaf aria-hidden="true" className="pointer-events-none absolute -right-12 top-8 h-72 w-72 rotate-12 text-white/[0.03]">
        <LeafMark className="h-full w-full" />
      </span>

      <div className="container-x relative">
        <Reveal as="header" className="max-w-2xl" y={24} fade={false}>
          <Eyebrow tone="light">Featured products</Eyebrow>
          <h2 id="product-range-heading" className="mt-4 font-display text-h1 font-semibold text-white [text-wrap:balance]">
            A closer look at the range.
          </h2>
          <p className="mt-4 text-lead text-white/70">
            Explore GreenbayBio bio-fertilizers, biostimulants and plant-strength solutions developed for different
            crops and agricultural requirements — each with its composition, usage and pack sizes.
          </p>
        </Reveal>

        {isLoading ? (
          <div className="mt-12 flex justify-center text-brand-300"><Spinner /></div>
        ) : (
          <Reveal className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" stagger={0.1} y={30} fade={false}>
            {products.map((product) => (
              <FeaturedCard key={product.id} product={product} />
            ))}
          </Reveal>
        )}

        <Reveal className="mt-10" y={16} fade={false}>
          <Link to="/catalog" className="link-underline inline-flex items-center gap-2 font-semibold text-brand-300">
            View all products
            <Icon name="ArrowRight" className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
